import { ChangeEvent, FC, FormEvent, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { ArrowForward as GoIcon, Visibility, VisibilityOff } from '@mui/icons-material';
import { IconButton, InputAdornment, InputLabel, TextField, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { Grid } from '@mui/system';
import { is } from 'date-fns/locale';
import { isUndefined } from 'lodash';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import validate from 'validate.js';
import { ERROR_401, ERROR_404, ERROR_429 } from 'common/config/api';
import { logIn } from 'common/config/urls';
import { FORGOT_RESET_PASSWORD } from 'common/constants/login';
import * as messages from 'common/constants/messages/validations';
import { useCookieConsent } from 'common/providers/CookieConsentProvider';
import { Alert } from 'components';
import { useSSO } from 'pages/Login/hooks';
import PMActionButtons from 'pages/ProcessManagement/components/PMActionButtons';
import { useGetAccessTokens } from 'services/hooks/auth';
import { giveFocusToInputWithDelay, Logger } from 'utilities';
import SocialLogin from '../SocialLogin';
import { FormState, LogInFormProps } from './LogInForm.props';
import {
  ContentWrapper,
  FooterWrapper,
  ForgotPasswordLink,
  FormPaper,
  ManageCookieLink,
  OrText,
  PageWrapper,
  SignupLink,
  SubmitButton,
  TermsRow,
} from './LogInForm.styles';
import { getFormVariables } from './LogInForm.utils';
import { constraints } from './LogInForm.validation';

const LogInForm: FC<LogInFormProps> = ({
  location,
  setPasswordRecovery,
  isProcessManagementForm = false,
  isLogInFormVisible = true,
  setIsLogInFormVisible,
}) => {
  const navigate = useNavigate();
  const getAccessTokens = useGetAccessTokens();
  const { showPreferences } = useCookieConsent();
  const [isAlertVisible, setIsAlertVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(messages.FIX_BEFORE_CONTINUE);
  const [formState, setFormState] = useState<FormState>({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
  });
  const usernameInputRef = useRef<HTMLInputElement>(null) as RefObject<HTMLInputElement>;
  const passwordInputRef = useRef<HTMLInputElement>(null) as RefObject<HTMLInputElement>;
  const { isSSOMode, discoverTenantByEmail } = useSSO();

  useEffect(() => {
    if (isLogInFormVisible) {
      if (isSSOMode) {
        giveFocusToInputWithDelay(usernameInputRef);
      } else {
        giveFocusToInputWithDelay(passwordInputRef);
      }
    }
  }, [isLogInFormVisible, isSSOMode]);

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  const hasError = (field: keyof FormState['values']) =>
    !!formState.touched[field] && !!formState.errors[field] && !!formState.errors[field]?.length;

  const handleLogIn = async (event: FormEvent) => {
    try {
      event.preventDefault();
      setIsAlertVisible(false);

      if (!formState.isValid) {
        setIsAlertVisible(true);
        setErrorMessage(messages.FIX_BEFORE_CONTINUE);
        return;
      }

      setIsLoading(true);

      const securePortalUrl = location.state?.isSecurePortal ? location.state.from : null;
      const hasDeepLink = location.state?.from?.pathname && location.state?.from?.search;

      const callback = () => {
        if (securePortalUrl) {
          navigate(securePortalUrl);
        } else if (hasDeepLink) {
          // For deep links, navigate directly to the target URL
          navigate(`${location.state.from.pathname}${location.state.from.search}`);
        } else {
          const { from } = location.state ?? { from: { pathname: logIn } };
          if (location.pathname !== from.pathname && location.state?.from?.pathname && location.state?.from?.search) {
            navigate(`${location.state.from.pathname}${location.state.from.search}`);
          }
        }
      };
      const response = await getAccessTokens(formState.values.mail ?? '', formState.values.password ?? '', callback);

      const statusCode = response.statusCode?.toString() ?? ERROR_404;

      if (!response || [ERROR_401, ERROR_429].includes(statusCode)) {
        let errorMsg = messages.UNABLE_TO_LOGIN_TRY_AGAIN;
        switch (statusCode) {
          case ERROR_401:
            errorMsg = response.body?.detail ?? messages.WRONG_USERNAME_OR_PASSWORD;
            break;
          case ERROR_429:
            errorMsg = messages.TOO_MANY_REQUESTS(response.body.detail.match(/\d+/)[0]);
            break;
          default:
            break;
        }

        setIsAlertVisible(true);
        setErrorMessage(errorMsg);
      }
    } catch (error) {
      Logger.error('Error logging in', error);
      setIsAlertVisible(true);
      setErrorMessage(messages.UNABLE_TO_LOGIN_TRY_AGAIN);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const tmpFormState = { ...formState };
      const fieldName = event.target.name as keyof FormState['values'];
      const fieldValue = event.target.value;
      const errors = validate({ ...formState.values, [fieldName]: fieldValue }, constraints);

      tmpFormState.values[fieldName] = fieldValue;
      tmpFormState.touched[fieldName] = true;
      tmpFormState.isValid = isUndefined(errors);
      tmpFormState.errors = errors ?? {};

      setFormState(tmpFormState);
    },
    [formState]
  );

  const forgotPasswordCallback = useCallback(() => {
    setPasswordRecovery?.(true);
  }, [setPasswordRecovery]);

  const handleSetIsLogInFormVisible = () => {
    setIsLogInFormVisible?.(true);
  };

  const handleSSOConnect = useCallback(() => {
    if (formState.values.mail) {
      discoverTenantByEmail(formState.values.mail);
    }
  }, [formState.values.mail, discoverTenantByEmail]);

  return (
    <PageWrapper>
      <ContentWrapper>
        <FormPaper elevation={isProcessManagementForm ? 0 : 1} isProcessManagementForm={isProcessManagementForm}>
          {!isProcessManagementForm && <Typography variant="h2">Login</Typography>}
          <Alert isAlertVisible={isAlertVisible} severity="error">
            <>{errorMessage}</>
          </Alert>
          <form onSubmit={handleLogIn} data-testid="login-form">
            {isProcessManagementForm && isLogInFormVisible && (
              <InputLabel style={{ marginBottom: '0.5em' }}>Username</InputLabel>
            )}
            {isLogInFormVisible && (
              <TextField
                id="login-username-input"
                name="mail"
                label={getFormVariables(isProcessManagementForm).userLabel}
                fullWidth
                type="text"
                className="scar-textField"
                error={isAlertVisible && hasError('mail')}
                required
                helperText={isAlertVisible && hasError('mail') ? formState.errors.mail?.[0] : null}
                onChange={handleChange}
                onKeyDown={e => {
                  if (e.key === 'Enter' && isSSOMode) {
                    e.preventDefault();
                    handleSSOConnect();
                  }
                }}
                variant={getFormVariables(isProcessManagementForm).variant}
                inputRef={usernameInputRef}
              />
            )}
            {!isSSOMode && isProcessManagementForm && isLogInFormVisible && (
              <InputLabel style={{ marginBottom: '0.5em' }}>Password</InputLabel>
            )}
            {!isSSOMode && isLogInFormVisible && (
              <TextField
                id="login-password-input"
                name="password"
                label={getFormVariables(isProcessManagementForm).passwordLabel}
                fullWidth
                type={showPassword ? 'text' : 'password'}
                className="scar-textField"
                error={isAlertVisible && hasError('password')}
                required
                helperText={isAlertVisible && hasError('password') ? formState.errors.password?.[0] : null}
                onChange={handleChange}
                inputRef={passwordInputRef}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          id="login-show-password-btn"
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPassword}
                          size="small">
                          {showPassword ? <Visibility fontSize="inherit" /> : <VisibilityOff fontSize="inherit" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
                variant={getFormVariables(isProcessManagementForm).variant}
              />
            )}
            {isSSOMode && isLogInFormVisible && (
              <SubmitButton
                id="sso-connect-btn"
                variant="contained"
                color="secondary"
                fullWidth
                onClick={handleSSOConnect}
                disabled={isLoading || !formState.values.mail}
                type="button">
                {isLoading ? <CircularProgress size={28} color="inherit" /> : 'Next'}
              </SubmitButton>
            )}
            {isProcessManagementForm ? (
              <PMActionButtons handleOnClick={isLogInFormVisible ? handleLogIn : handleSetIsLogInFormVisible} />
            ) : (
              <>
                {!isSSOMode && (
                  <SubmitButton
                    id="login-action-btn"
                    variant="contained"
                    color="secondary"
                    fullWidth
                    endIcon={!isLoading && <GoIcon />}
                    type="submit">
                    {isLoading ? <CircularProgress size={28} color="inherit" /> : 'Log In'}
                  </SubmitButton>
                )}
                <OrText component="p" variant="h3">
                  OR
                </OrText>
                <SocialLogin setAlert={setIsAlertVisible} setError={setErrorMessage} />
              </>
            )}
          </form>
        </FormPaper>

        {!isProcessManagementForm && (
          <ForgotPasswordLink id="forgot-password-link" onClick={forgotPasswordCallback} variant="h5">
            {FORGOT_RESET_PASSWORD}
          </ForgotPasswordLink>
        )}
      </ContentWrapper>

      {!isProcessManagementForm && (
        <FooterWrapper>
          <Grid size={{ lg: 6, xs: 12 }} sx={{ textAlign: 'center' }}>
            <Typography color="textSecondary" variant="body1">
              New to Scalar?
              <SignupLink id="signup-link" component={RouterLink} to="/sign-up" variant="body1">
                Sign Up!
              </SignupLink>
            </Typography>
          </Grid>
          <TermsRow>
            <ManageCookieLink
              className="nsc-bara-manage-cookie-settings"
              onClick={e => {
                e.preventDefault();
                showPreferences();
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  showPreferences();
                }
              }}
              tabIndex={0}>
              Manage Cookie Preferences
            </ManageCookieLink>
            <a href="https://scalar.io/privacy-policy/" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            <a href="https://scalar.io/terms-of-service/" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
          </TermsRow>
        </FooterWrapper>
      )}
    </PageWrapper>
  );
};

export default LogInForm;
